import { formatDateBR } from '../utils/dateUtils';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EyeIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { assinaturasService } from '../services/assinaturas.service';
import { formatCurrency } from '../utils/formatters';
import DataTable from '../components/DataTable/DataTable';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';
import type { Column } from '../types/data-table';
import type { FilterState } from '../types/data-table';

type Assinatura = import('../services/assinaturas.service').Assinatura;

import { useEmpresa } from '../contexts/EmpresaContext';

export default function Assinaturas() {
  const { empresa } = useEmpresa();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filters, setFilters] = useState<FilterState>({
    enabled: 'all',
    search: '',
    dateCreatedFrom: '',
    dateCreatedTo: '',
    dateUpdatedFrom: '',
    dateUpdatedTo: '',
  });

  const apiFilters = useMemo(() => {
    const params: Record<string, unknown> = {
      page,
      limit,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    return params;
  }, [page, limit, filters]);

  const { data: response, isLoading } = useQuery({
    queryKey: ['assinaturas', empresa?.id, apiFilters],
    queryFn: () => assinaturasService.findAll(apiFilters),
    enabled: !!empresa?.id,
  });

  const assinaturas = response?.data || [];
  const pagination = response?.meta || {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  };

  const handleView = (assinatura: Assinatura) => {
    navigate(`/assinaturas/${assinatura.id}`);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      ACTIVE: { label: 'Ativa', className: 'bg-green-100 text-green-800' },
      SUSPENDED: { label: 'Suspensa', className: 'bg-yellow-100 text-yellow-800' },
      CANCELED: { label: 'Cancelada', className: 'bg-red-100 text-red-800' },
      EXPIRED: { label: 'Expirada', className: 'bg-gray-100 text-gray-800' },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const columns: Column<Assinatura>[] = [
    {
      key: 'name',
      label: 'Nome',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => getStatusBadge(item.status),
      sortable: true,
    },
    {
      key: 'plan',
      label: 'Plano',
      render: (item) => item.plan?.name || 'N/A',
      sortable: false,
    },
    {
      key: 'billing_cycle',
      label: 'Ciclo de Cobrança',
      render: (item) => {
        const cycles: Record<string, string> = {
          MONTHLY: 'Mensal',
          QUARTERLY: 'Trimestral',
          YEARLY: 'Anual',
        };
        return cycles[item.billing_cycle] || item.billing_cycle;
      },
      sortable: false,
    },
    {
      key: 'contracted_lives',
      label: 'Vidas Contratadas',
      sortable: true,
    },
    {
      key: 'wallet_amount',
      label: 'Saldo da Carteira',
      render: (item) => item.is_unlimited_wallet 
        ? 'Ilimitado' 
        : formatCurrency(Number(item.wallet_amount)),
      sortable: false,
    },
    {
      key: 'started_at',
      label: 'Início',
      render: (item) => formatDateBR(item.started_at),
      sortable: true,
    },
    {
      key: 'expires_at',
      label: 'Expira em',
      render: (item) => item.expires_at 
        ? formatDateBR(item.expires_at)
        : 'N/A',
      sortable: false,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Assinaturas"
        description="Gerencie suas assinaturas e planos contratados."
      />

      <DataTable
        data={assinaturas}
        columns={columns}
        loading={isLoading}
        getId={(item) => item.id}
        emptyMessage="Nenhuma assinatura encontrada."
        pagination={pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        onFiltersChange={setFilters}
        actions={(item) => (
          <button
            onClick={() => handleView(item)}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <EyeIcon className="h-4 w-4" />
            Ver Detalhes
          </button>
        )}
      />
    </PageContainer>
  );
}
